import { AnalyticsEvent } from './models';
import { Config } from '@/environment';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { MUTATIONS } from '@/store/adobe';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { Product, Value, WebInteraction } from './platforms/adobe-analytics/models';
import { EventEmitter } from 'events';
import { LinkClickEvent } from './events';
import { AdobeCartAddEvent, AdobeCartViewEvent } from './platforms/adobe-analytics/adobe-events';
import { UpsellItem } from '@/models';

export const adobeEventEmitter = new EventEmitter();

export interface AnalyticsService {
  send(event: AnalyticsEvent): void;
}
export interface AnalyticsService {
  send(event: AnalyticsEvent): void;

  // Temp storage for Adobe Analytics events that trigger before the 
  // AA script is loaded.
  addAdobeAnalyticsEventsToStorageQueue(event: AnalyticsEvent): void;

  // Once AA script has loaded, and events are 
  // fired, clear events from store
  removeAdobeAnalyticsEventsFromStore(): void;

  // Emit analytics events if Adobe Data 
  // Layer is available and customer profile 
  // has been hydrated on initial app load
  emitQueuedAnalyticsEvents(): void;

  // If internal link, store 'linkClicked' event until it has been appended to subsequent 
  // 'pageLoaded' event in order to reduce Adobe server calls. If external link, send the
  // 'linkClicked' event right away
  addLinkClickedEventToStore(linkClickedEvent: WebInteraction, linkUrl?: string): void;

  // Remove 'linkClicked' event from store
  // after it has been appended to subsequent
  // 'pageLoaded' event
  removeLinkClickedEventFromStore(): void;

  // Store addToBagEvent until subsequent CartPageLoadedEvent has fired.
  setAddToBagEventToStore(addToBagEvent: AdobeCartAddEvent): void;
  removeAddToBagEventFromStore(): void;

  // Store bagViewEvent until subsequent CartPageLoadedEvent has fired.
  setBagViewEventToStore(bagViewEvent: AdobeCartViewEvent): void;
  removeBagViewEventFromStore(): void;

  setUpsellImpressionToStore(upsellImpression: Product[]): void;
  removeUpsellImpressionFromStore(): void;

  setHasUpsellImpressionChanged(hasChanged: boolean): void;

  // Store 'registrationComplete' until 2SV completes and append it to subsequent
  // 'pageLoaded' event
  addRegistrationCompleteEventToStore(registrationCompleteEvent: Value): void;

  // Remove 'registrationComplete' event from store after it has been appended to
  // subsequent 'pageLoaded' event
  removeRegistrationCompleteEventFromStore(): void;

  // Store 'rewardsEnroll' and append it to subsequent 'pageLoaded' event
  addRewardsEnrollEventToStore(rewardsEnrollEvent: Value): void;

  // Remove 'rewardsEnroll' event from store after it has been appended to
  // subsequent 'pageLoaded' event
  removeRewardsEnrollEventFromStore(): void;

  // We only want to send certain pageLoaded events once per session. Call this
  // function the first time the corresponding page is viewed.
  setHomePageHasBeenViewed(): void;
  setNutritionCalcPageHasBeenViewed(): void;
}

export const EMITTER = new EventEmitter();
export class AppAnalyticsService implements AnalyticsService {

  constructor(private config: Config, private store: Store<AppState>) { }

  send(event: AnalyticsEvent) {
   
    // isLoggedIn() returns boolean | undefined
    if (!window.adobeDataLayer || this.isLoggedIn() == undefined) {
      //  1. On initial app load, if data layer is not available the event needs to be stored until the 
      //  data layer is available. 
      //  2. Also, if customer jwt is still valid and the customer service has not 
      //  yet hydrated the customer profile, isLoggedIn() will return 'undefined'. The app
      //  needs to store the event until the customer profile has been hydrated.
      this.addAdobeAnalyticsEventsToStorageQueue(event);
    } else {
      //  If isLoggedIn() returns false, this means the jwt is not valid or non-existent and therefore
      //  the customer is not logged in. The event can be emitted because there is no customer profile
      //  to wait for.
      //  If isLoggedIn() returns true, the customer profile is hydrated and the event can be emitted.
      adobeEventEmitter.emit(event.name, event);
    }
  }

  isLoggedIn(): boolean | undefined {
    return this.store.getters[CUSTOMER_GETTERS.IS_LOGGED_IN];
  }

  emitQueuedAnalyticsEvents(): void {
    if (window.adobeDataLayer && this.isLoggedIn != undefined) {
      this.store.state.adobe.adobeDataLayerEvents.forEach(event => {
        this.send(event);
      }); 
      this.removeAdobeAnalyticsEventsFromStore(); // clear queue after events have been fired.
    }
  }

  addAdobeAnalyticsEventsToStorageQueue(event: AnalyticsEvent): void {
    this.store.commit(MUTATIONS.SET_ADOBE_DATA_LAYER_EVENTS, event);
  }

  removeAdobeAnalyticsEventsFromStore(): void {
    this.store.commit(MUTATIONS.SET_ADOBE_DATA_LAYER_EVENTS, []);
  }

  addLinkClickedEventToStore(linkClickedEvent: WebInteraction, linkUrl?: string): void {
    if (linkUrl && !this.isInternalLink(linkUrl)) {
      this.send(new LinkClickEvent(linkClickedEvent));
    } else {
      this.store.commit(MUTATIONS.SET_LINK_CLICKED_EVENT, linkClickedEvent);
    }
  }

  removeLinkClickedEventFromStore(): void {
    this.store.commit(MUTATIONS.SET_LINK_CLICKED_EVENT, null);
  }

  setAddToBagEventToStore(addToBagEvent: AdobeCartAddEvent): void {
    this.store.commit(MUTATIONS.SET_ADD_TO_BAG_EVENT, addToBagEvent);
  }

  removeAddToBagEventFromStore(): void {
    this.store.commit(MUTATIONS.SET_ADD_TO_BAG_EVENT, null);
  }

  setBagViewEventToStore(bagViewEvent: AdobeCartViewEvent): void {
    this.store.commit(MUTATIONS.SET_BAG_VIEW_EVENT, bagViewEvent);
  }

  removeBagViewEventFromStore(): void {
    this.store.commit(MUTATIONS.SET_BAG_VIEW_EVENT, null);
  }

  setUpsellImpressionToStore(upsellImpression: Product[]): void {
    this.store.commit(MUTATIONS.SET_UPSELL_IMPRESSION, upsellImpression);
  }

  removeUpsellImpressionFromStore(): void {
    this.store.commit(MUTATIONS.SET_UPSELL_IMPRESSION, null);
  }

  setHasUpsellImpressionChanged(hasChanged: boolean): void {
      this.store.commit(MUTATIONS.SET_HAS_UPSELL_IMPRESSION_CHANGED, hasChanged);
  }

  addRegistrationCompleteEventToStore(registrationCompleteEvent: Value): void {
    this.store.commit(MUTATIONS.SET_REGISTRATION_COMPLETE_EVENT, registrationCompleteEvent);
  }

  removeRegistrationCompleteEventFromStore(): void {
    this.store.commit(MUTATIONS.SET_REGISTRATION_COMPLETE_EVENT, null);
  }

  addRewardsEnrollEventToStore(rewardsEnrollEvent: Value): void {
    this.store.commit(MUTATIONS.SET_REWARDS_ENROLL_EVENT, rewardsEnrollEvent);
  }

  removeRewardsEnrollEventFromStore(): void {
    this.store.commit(MUTATIONS.SET_REWARDS_ENROLL_EVENT, null);
  }

  setHomePageHasBeenViewed(): void {
    this.store.commit(MUTATIONS.SET_HAS_HOME_PAGE_BEEN_VIEWED, true);
  }

  setNutritionCalcPageHasBeenViewed(): void {
    this.store.commit(MUTATIONS.SET_HAS_NUTRITION_CALC_PAGE_BEEN_VIEWED, true);
  }

  private isInternalLink(linkUrl: string): boolean {
    const firstChar = linkUrl[0];
    return firstChar === '/' || firstChar === '#';
  }
}
